import React, { useState, useEffect } from "react";
import { SxProps, Theme } from "@mui/system";
import {
    Autocomplete,
    TextField,
    CircularProgress,
    InputAdornment,
    Box,
    Typography,
} from "@mui/material";
import InfoButton from "../../Tooltip/InfoTextField";
import { VariantOption } from "../../../Types/CommonTypes";
import { useAuth } from "../../../Context/AuthContext";
import { useTranslation } from "react-i18next";

interface VariantDropdownProps {
    label?: string;
    itemNo: string | null;
    onChange: (value: VariantOption | null) => void;
    valueId?: number | null;
    initialOptions?: VariantOption[];
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    infoText?: string;
    sx?: SxProps<Theme>;
    onKeyDown?: (e: React.KeyboardEvent) => void;
}

const VariantAutoComplete: React.FC<VariantDropdownProps> = ({
    label,
    itemNo,
    onChange,
    valueId,
    initialOptions = [],
    disabled = false,
    error,
    helperText,
    infoText,
    sx,
    onKeyDown,
}) => {
    const { authService } = useAuth();
    const { t } = useTranslation();
    const [options, setOptions] = useState<VariantOption[]>(initialOptions);
    const [loading, setLoading] = useState(false);
    const [selectedValue, setSelectedValue] = useState<VariantOption | null>(null);
    const [highlighted, setHighlighted] = useState<VariantOption | null>(null);
    const fetchVariants = async (query: { itemNo: string }): Promise<VariantOption[]> => {
        const response = await authService.post<{ items: VariantOption[] }>("/itemApi/GetVariantsForItem", query);
        return response?.items || [];
    };

    useEffect(() => {
        const loadVariants = async () => {
            if (!itemNo) return;

            if (!valueId) {
                setSelectedValue(null);
                
            }

            // Check if valueId exists in current options
            const matched = valueId
                ? options.find((v) => v.id == valueId && v.itemNo == itemNo) || null
                : null;

            if (matched) {
                setSelectedValue(matched);
                return;
            }

            setLoading(true);
            try {
                const variants = await fetchVariants({ itemNo });
                setOptions(variants);

                const match = valueId
                    ? variants.find((v) => v.id == valueId) || null
                    : null;

                setSelectedValue(match);
            } catch (error) {
                console.error("Failed to load variants", error);
            } finally {
                setLoading(false);
            }
        };

        loadVariants();
    }, [itemNo, valueId]);


    const handleKeyDownInternal = (e: React.KeyboardEvent) => {
        // If user hits Enter/Tab and we have options, pick the first one
        if (( e.key === "Tab") && options.length > 0) {
            if (highlighted) {
                e.preventDefault(); // keep focus here long enough to select
                e.stopPropagation();
                setSelectedValue(highlighted);
                onChange(highlighted);
            }
        }

        onKeyDown?.(e);
    };
    return (
        <Autocomplete
            options={options}
            getOptionLabel={(option) => `${option.itemNo} - ${option.colourDescription || "N/A"}`}
            value={selectedValue}
            onChange={(_, newValue) => {
                setSelectedValue(newValue);
                onChange(newValue);
            }}
            disabled={disabled || !itemNo}
            loading={loading}
            autoHighlight
            onHighlightChange={(_, option) => setHighlighted(option as VariantOption | null)}
            onKeyDown={handleKeyDownInternal}
            sx={{ width: "100%", ...sx }}
            renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body1">
                        <strong>{option.id} - {option.itemNo}</strong> - {option.colourDescription}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t("common.size")}: {option.sizeDescription || "N/A"}, {t("common.length")}: {option.lengthDescription || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t("common.barcode")}: {option.barcode || "N/A"}
                    </Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label ?? t("common.variant")}
                    error={error}
                    helperText={helperText}
                    InputLabelProps={{ shrink: true }}
               
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {params.InputProps.endAdornment}
                                {infoText && (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={infoText} />
                                    </InputAdornment>
                                )}
                                {loading && <CircularProgress color="inherit" size={20} />}
                            </>
                        ),
                        autoComplete: "new-password",
                    }}
                />
            )}
        />
    );
};

export default VariantAutoComplete;